import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ProfileCard from "@/components/ProfileCard";
import ReferralRewardCard from "@/components/ReferralRewardCard";
import { Button } from "@/components/ui/button";
import { Sparkle, Heart, ChatCircle, Crown, ArrowRight, Warning, UsersThree, Camera } from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [matches, setMatches] = useState([]);
  const [interestsRecv, setInterestsRecv] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [m, ir, me] = await Promise.all([
          api.get("/matches/suggested?limit=6"),
          api.get("/interests/received"),
          api.get("/profiles/me"),
        ]);
        setMatches(m.data.matches || []);
        setInterestsRecv(ir.data.interests || []);
        setProfile(me.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const completeness = React.useMemo(() => {
    if (!profile) return 0;
    const fields = ["dob", "height_cm", "religion", "community", "mother_tongue", "city", "state", "education", "profession", "about_me", "family_status"];
    const filled = fields.filter((f) => profile[f]).length;
    const hasPhoto = profile.photos && profile.photos.length > 0 ? 1 : 0;
    return Math.round(((filled + hasPhoto) / (fields.length + 1)) * 100);
  }, [profile]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="label-eyebrow text-brand-terracotta">Assalamu alaikum</div>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tighter mt-2 text-brand-ink">
            {user?.name?.split(" ")[0]}
          </h1>
          <p className="mt-3 text-brand-muted">Here's your matrimony world today.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/plans" data-testid="dashboard-plans-cta" className="text-sm text-brand-terracotta underline underline-offset-4">
            {user?.plan && user.plan !== "free" ? `${user.plan.toUpperCase()} member` : "Upgrade membership"}
          </Link>
        </div>
      </div>

      {/* Photo required nudge */}
      {profile && !profile.primary_photo && (
        <div
          className="mt-8 bg-brand-terracotta text-white p-6 md:p-7 flex items-center gap-4 flex-wrap"
          data-testid="dashboard-photo-nudge"
        >
          <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
            <Camera size={22} weight="duotone" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-[11px] uppercase tracking-widest text-white/80">Action required</div>
            <div className="font-serif text-xl md:text-2xl mt-1 leading-tight">Add your primary photograph.</div>
            <div className="text-sm text-white/80 mt-1">
              A photo is required before you can send interests. It takes 30 seconds.
            </div>
          </div>
          <Button
            onClick={() => nav("/my-profile")}
            data-testid="dashboard-photo-nudge-btn"
            className="rounded-full bg-white text-brand-terracotta hover:bg-white/90 font-semibold"
          >
            Add photo <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>
      )}

      {/* Bento grid */}
      <div className="mt-10 grid grid-cols-12 gap-4">
        {/* Completeness */}
        <div className="col-span-12 md:col-span-6 lg:col-span-4 bg-white border border-brand-line p-8" data-testid="dashboard-completeness">
          <div className="label-eyebrow text-brand-muted">Profile completeness</div>
          <div className="mt-4 flex items-baseline gap-2">
            <div className="font-serif text-6xl text-brand-ink tracking-tighter num-lining">{completeness}%</div>
            <span className="text-brand-muted">complete</span>
          </div>
          <div className="mt-4 h-1.5 bg-brand-line overflow-hidden">
            <div className="h-full bg-brand-terracotta transition-all duration-700" style={{ width: `${completeness}%` }} />
          </div>
          {completeness < 100 && (
            <div className="mt-5 flex items-center gap-2 text-sm text-brand-muted">
              <Warning size={16} className="text-brand-gold" /> Complete your dossier for better matches.
            </div>
          )}
          <Button onClick={() => nav("/my-profile")} data-testid="dashboard-edit-profile-btn" className="mt-6 rounded-full bg-brand-ink hover:bg-brand-ink/90 text-white">
            Edit dossier <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>

        {/* Interests received */}
        <div className="col-span-12 md:col-span-6 lg:col-span-4 bg-white border border-brand-line p-8" data-testid="dashboard-interests-count">
          <Heart size={22} weight="duotone" className="text-brand-terracotta" />
          <div className="mt-4 flex items-baseline gap-2">
            <div className="font-serif text-6xl text-brand-ink tracking-tighter num-lining">{interestsRecv.length}</div>
            <span className="text-brand-muted text-sm">received</span>
          </div>
          <div className="text-xs uppercase tracking-widest text-brand-muted mt-2">Interests</div>
          <Button variant="ghost" onClick={() => nav("/interests")} data-testid="dashboard-interests-cta" className="mt-4 px-0 hover:bg-transparent text-brand-terracotta">
            View interests <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>

        {/* Actions */}
        <div className="col-span-12 md:col-span-6 lg:col-span-4 bg-brand-terracotta text-white p-8 flex flex-col justify-between">
          <div>
            <Sparkle size={22} weight="duotone" />
            <h3 className="font-serif text-3xl mt-4 leading-tight">Discover your curated matches</h3>
            <p className="mt-2 text-white/80 text-sm">Filter by community, profession, and location.</p>
          </div>
          <Button
            onClick={() => nav("/browse")}
            data-testid="dashboard-browse-cta"
            className="mt-6 self-start rounded-full bg-white text-brand-terracotta hover:bg-white/90"
          >
            Browse Profiles <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>

        {/* Messages */}
        <div className="col-span-12 md:col-span-6 lg:col-span-4 bg-white border border-brand-line p-8">
          <ChatCircle size={22} weight="duotone" className="text-brand-terracotta" />
          <h3 className="font-serif text-2xl mt-4 text-brand-ink">Conversations</h3>
          <p className="mt-2 text-sm text-brand-muted">Chats begin only after mutual interest is accepted.</p>
          <Button variant="ghost" onClick={() => nav("/messages")} data-testid="dashboard-messages-cta" className="mt-4 px-0 hover:bg-transparent text-brand-terracotta">
            Open inbox <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>

        {/* Membership */}
        <div className="col-span-12 md:col-span-6 lg:col-span-4 bg-white border border-brand-line p-8">
          <Crown size={22} weight="duotone" className="text-brand-gold" />
          <h3 className="font-serif text-2xl mt-4 text-brand-ink">Membership</h3>
          <p className="mt-2 text-sm text-brand-muted">
            Current: <span className="text-brand-ink font-medium">{(user?.plan || "free").toUpperCase()}</span>
          </p>
          <Button variant="ghost" onClick={() => nav("/plans")} data-testid="dashboard-membership-cta" className="mt-4 px-0 hover:bg-transparent text-brand-terracotta">
            View plans <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>

        {/* Invite family */}
        <div className="col-span-12 md:col-span-6 lg:col-span-4 bg-brand-gold/10 border border-brand-gold/30 p-8" data-testid="dashboard-invite-tile">
          <UsersThree size={22} weight="duotone" className="text-brand-terracotta" />
          <h3 className="font-serif text-2xl mt-4 text-brand-ink">Grow the family</h3>
          <p className="mt-2 text-sm text-brand-muted">
            Invite parents, siblings, or friends. Every good match starts with a good introduction.
          </p>
          <Button onClick={() => nav("/invite")} data-testid="dashboard-invite-cta" className="mt-4 rounded-full bg-brand-terracotta hover:bg-brand-terracotta/90 text-white">
            Invite family <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>

        {/* Referral rewards — 3 friends -> 1 month Silver */}
        <ReferralRewardCard />
      </div>

      {/* Suggested matches */}
      <section className="mt-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="label-eyebrow text-brand-terracotta">Curated for you</div>
            <h2 className="font-serif text-4xl mt-2 text-brand-ink tracking-tight">Suggested matches</h2>
          </div>
          <Link to="/browse" className="text-sm text-brand-terracotta underline underline-offset-4" data-testid="see-all-matches-link">See all</Link>
        </div>
        {loading ? (
          <div className="text-brand-muted">Curating matches…</div>
        ) : matches.length === 0 ? (
          <div className="text-brand-muted border border-dashed border-brand-line p-12 text-center" data-testid="no-matches-empty">
            Complete your profile to receive tailored suggestions.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="suggested-matches-grid">
            {matches.map((m) => <ProfileCard key={m.id} profile={m} />)}
          </div>
        )}
      </section>
    </div>
  );
}
